// ===========================
//  CustomerListPage.jsx (CRM v2)
// ===========================
import React, { useEffect, useState } from "react";

import CustomerFilter, { getDefaultCustomerFilters } from "./CustomerFilter";
import CustomerMiniDashboard from "./CustomerMiniDashboard";
import Pagination from "../../../components/common/Pagination";

import { 
  getAllCustomers, 
  deleteCustomer, 
  getCustomerById,
  getCustomerFilterOptions,
  getCustomerStats,
} from "../../../api/crm/customers/customerInfo";

import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { confirmDeleteCustomer } from "./confirmCustomerDelete";
import CustomerCreateModal from "./CustomerCreateModal";
import CustomerDetailModal from "./CustomerDetailModal";
import CustomerImportModal from "./CustomerImportModal";
import { formatDateTimeVN } from "../../../utils/formatUtils";
import CustomerTagsModal from "./tags/CustomerTagsModal";
import { hasPermission } from "../../../utils/permissionUtils";
import {
  Building2,
  ChevronRight,
  Phone,
  MapPin,
  UserRound,
  Trash2,
  Users,
} from "lucide-react";


const leadSourceLabels = {
  Facebook: "Facebook",
  Website: "Website",
  TikTok: "TikTok",
  Referral: "Giới thiệu",
  Event: "Workshop / Sự kiện",
};

const statusLabels = {
  new: "Mới",
  active: "Đang chăm sóc",
  idle: "Không tương tác",
  lost: "Mất khách",
};

const statusBadgeClasses = {
  new: "bg-blue-50 text-blue-700 border-blue-100",
  active: "bg-green-50 text-green-700 border-green-100",
  idle: "bg-yellow-50 text-yellow-700 border-yellow-100",
  lost: "bg-red-50 text-red-700 border-red-100",
};

const recordStatusLabels = {
  active: "Đang hoạt động",
  closed: "Đã đóng",
  merged: "Đã sáp nhập",
};

const recordStatusBadgeClasses = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-100",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  merged: "bg-violet-50 text-violet-700 border-violet-100",
};


const CustomerListPage = () => {

  // ========================
  // STATE
  // ========================
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState(() => getDefaultCustomerFilters());
  const [branchOptions, setBranchOptions] = useState([]);
  const [wardOptions, setWardOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [recordStatusOptions, setRecordStatusOptions] = useState([]);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  const [customerTypeOptions, setCustomerTypeOptions] = useState([]);
  const [schoolLevelOptions, setSchoolLevelOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);

  const [showDrawer, setShowDrawer] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();
  const defaultImportBranchId = currentUser?.branch?.id || currentUser?.branch_id || "";
  const importBranchOptions = (() => {
    const fallbackId = currentUser?.branch?.id || currentUser?.branch_id;
    const fallbackLabel =
      currentUser?.branch?.name || currentUser?.branch_name || currentUser?.branch?.branch_name;
    if (!fallbackId || !fallbackLabel) return branchOptions;
    if (branchOptions.some((opt) => String(opt.value) === String(fallbackId))) return branchOptions;
    return [{ value: fallbackId, label: fallbackLabel }, ...branchOptions];
  })();

  // ========================
  // FETCH LIST
  // ========================
  const fetchCustomers = async () => {
    try {
      const res = await getAllCustomers({
        ...filters,
        page,
        pageSize,
      });

      setCustomers(res.items);
      setTotal(res.total);

    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải danh sách khách hàng");
    }
  };

  const fetchStats = async (f = filters) => {
    setStatsLoading(true);
    try {
      const data = await getCustomerStats(f);
      setStats(data);
    } catch (err) {
      console.error(err);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const refreshData = async () => {
    await Promise.all([fetchCustomers(), fetchStats(filters)]);
  };

  useEffect(() => {
    fetchCustomers();
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchStats(filters);
  }, [
    filters.keyword,
    filters.branch_id,
    filters.ward,
    filters.status,
    filters.record_status,
    filters.owner_id,
    filters.lead_source,
    filters.customer_type,
    filters.school_level,
    filters.tag_id,
  ]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await getCustomerFilterOptions({
          branch_id: filters.branch_id || undefined,
        });

        setBranchOptions(Array.isArray(res?.branches) ? res.branches : []);
        setWardOptions(Array.isArray(res?.wards) ? res.wards : []);
        setStatusOptions(Array.isArray(res?.statuses) ? res.statuses : []);
        setRecordStatusOptions(
          Array.isArray(res?.record_statuses) ? res.record_statuses : []
        );
        setOwnerOptions(Array.isArray(res?.owners) ? res.owners : []);
        setLeadSourceOptions(
          Array.isArray(res?.lead_sources) ? res.lead_sources : []
        );
        setCustomerTypeOptions(
          Array.isArray(res?.customer_types) ? res.customer_types : []
        );
        setSchoolLevelOptions(
          Array.isArray(res?.school_levels) ? res.school_levels : []
        );
        setTagOptions(Array.isArray(res?.tags) ? res.tags : []);
      } catch (err) {
        console.error(err);
        showErrorToast("Không thể tải dữ liệu bộ lọc khách hàng");
      }
    };

    fetchFilterOptions();
  }, [filters.branch_id]);

  // ========================
  // HANDLERS
  // ========================

  const handleView = async (id) => {
    try {
      const data = await getCustomerById(id);
  
      if (!data) {
        showErrorToast("Không tìm thấy thông tin khách hàng");
        return;
      }
  
      setDetailCustomer(data);     // lưu dữ liệu
      setShowDetailModal(true);    // mở modal
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải thông tin khách hàng");
    }
  };

  const handleEdit = async (id) => {
    try {
  
      const data = await getCustomerById(id);
  
      setDetailCustomer(null);
      setShowDetailModal(false);   // đóng modal chi tiết
  
      setEditingCustomer(data);    // set data edit
      setShowDrawer(true);         // mở modal create/edit
  
    } catch (err) {
  
      console.error(err);
      showErrorToast("Không thể tải dữ liệu để chỉnh sửa");
  
    }
  };
  
  const performDelete = async (id) => {
    try {
      await deleteCustomer(id);
      showSuccessToast("Đã xoá khách hàng");
      setShowDetailModal(false);
      refreshData();
    } catch (err) {
      console.error(err);
      showErrorToast("Xoá khách hàng thất bại");
    }
  };

  const handleDelete = async (id) => {
    try {
      const data = await getCustomerById(id);
      const confirm = await confirmDeleteCustomer(data);
      if (confirm.isConfirmed) {
        await performDelete(id);
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải thông tin khách hàng để xóa");
    }
  };

  const canDelete = hasPermission("crm.customers", "D");

  const renderCustomerCard = (c) => (
    <div
      key={c.id}
      className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
    >
      <button
        type="button"
        onClick={() => handleView(c.id)}
        className="w-full text-left"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white bg-red-100 text-sm font-bold text-red-600">
            {c.name?.charAt(0)?.toUpperCase() || "K"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="truncate text-[15px] font-bold text-gray-900">
                  {c.name || "—"}
                </h3>
                <p className="mt-0.5 text-[11px] font-medium text-gray-500">
                  {[c.customer_type, c.school_level].filter(Boolean).join(" · ") ||
                    "Khách hàng"}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    statusBadgeClasses[c.status] ||
                    "border-gray-100 bg-gray-50 text-gray-500"
                  }`}
                >
                  {c.status ? statusLabels[c.status] : "—"}
                </span>
                {c.record_status && c.record_status !== "active" ? (
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      recordStatusBadgeClasses[c.record_status] ||
                      "border-gray-100 bg-gray-50 text-gray-500"
                    }`}
                  >
                    {recordStatusLabels[c.record_status] || c.record_status}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12px] text-gray-600">
              <div className="flex min-w-0 items-center gap-1.5">
                <Phone size={13} className="shrink-0 text-red-500" />
                <span className="truncate font-semibold text-gray-800">
                  {c.contact_phone || c.phone || "—"}
                </span>
              </div>
              <div className="flex min-w-0 items-center gap-1.5">
                <Building2 size={13} className="shrink-0 text-red-500" />
                <span className="truncate">{c.branch_name || "—"}</span>
              </div>
              <div className="flex min-w-0 items-center gap-1.5 text-gray-500">
                <UserRound size={13} className="shrink-0 text-blue-500" />
                <span className="truncate">
                  {c.owner_name || "Chưa phân công"}
                </span>
              </div>
              {c.ward ? (
                <div className="flex min-w-0 items-start gap-1.5 text-gray-500">
                  <MapPin size={13} className="mt-0.5 shrink-0" />
                  <span className="line-clamp-2">{c.ward}</span>
                </div>
              ) : (
                <div className="flex min-w-0 items-center gap-1.5 text-gray-400">
                  <MapPin size={13} className="shrink-0" />
                  <span className="truncate">—</span>
                </div>
              )}
            </div>

            {(c.tags?.length > 0 || c.lead_source) && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {c.tags?.slice(0, 3).map((t) => (
                  <span
                    key={t.id}
                    className="rounded-full px-2 py-[3px] text-[10px] font-bold text-white"
                    style={{ backgroundColor: t.color || "#9ca3af" }}
                  >
                    {t.name}
                  </span>
                ))}
                {(c.tags?.length || 0) > 3 ? (
                  <span className="rounded-full bg-gray-100 px-2 py-[3px] text-[10px] font-bold text-gray-500">
                    +{c.tags.length - 3}
                  </span>
                ) : null}
                {c.lead_source ? (
                  <span className="rounded border border-gray-200 bg-gray-50 px-2 py-[3px] text-[10px] font-bold uppercase text-gray-600">
                    {leadSourceLabels[c.lead_source] || c.lead_source}
                  </span>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </button>

      <div className="mt-3 flex items-center justify-between border-t border-gray-50 pt-2.5">
        <span className="text-[11px] text-gray-400">
          Cập nhật {c.updated_at ? formatDateTimeVN(c.updated_at) : "—"}
        </span>
        <div className="flex items-center gap-1">
          {canDelete ? (
            <button
              type="button"
              title="Xóa khách hàng"
              onClick={() => handleDelete(c.id)}
              className="inline-flex rounded-lg p-2 text-gray-400 active:bg-red-50 active:text-red-600"
            >
              <Trash2 size={16} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => handleView(c.id)}
            className="inline-flex items-center gap-0.5 rounded-lg px-2 py-1.5 text-[11px] font-bold text-red-700 active:bg-red-50"
          >
            Chi tiết
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );

  // ========================
  // UI RENDER
  // ========================
  return (
    <div className="p-3 pb-24 md:p-4 md:pb-4">
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div data-tour="customers-header">
          <div className="mb-1 flex items-center gap-3 sm:mb-2 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <Users className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Quản lý <span className="text-red-700">khách hàng</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Theo dõi, chăm sóc và quản lý khách hàng tập trung
              </p>
            </div>
          </div>
        </div>
      </div>

      <CustomerMiniDashboard stats={stats || {}} loading={statsLoading} />

      {/* FILTERS */}
      <CustomerFilter
        branchOptions={branchOptions}
        wardOptions={wardOptions}
        statusOptions={statusOptions}
        recordStatusOptions={recordStatusOptions}
        ownerOptions={ownerOptions}
        leadSourceOptions={leadSourceOptions}
        customerTypeOptions={customerTypeOptions}
        schoolLevelOptions={schoolLevelOptions}
        tagOptions={tagOptions}
        onRefresh={refreshData}
        onNewClick={() => {
          setEditingCustomer(null);
          setShowDrawer(true);
        }}
        onImportExcelClick={() => setShowImportModal(true)}
        onFilterChange={(newFilters) => {
          setFilters(newFilters);
          setPage(1);
        }}
        onManageTagsClick={() => setShowTagModal(true)}
      />

      {isMobile ? (
        <div className="space-y-3">
          {customers.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-100 bg-white py-14">
              <Users className="h-10 w-10 text-gray-300" />
              <p className="text-sm font-semibold text-gray-500">
                Không tìm thấy khách hàng phù hợp
              </p>
            </div>
          ) : (
            customers.map(renderCustomerCard)
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] table-auto border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-left">
                  <th className="w-[48px] p-4 text-center text-xs font-bold uppercase tracking-wider text-gray-400">
                    #
                  </th>
                  <th className="min-w-[220px] p-4 text-xs font-bold uppercase tracking-wider text-gray-400">
                    Khách hàng
                  </th>
                  <th className="min-w-[170px] p-4 text-xs font-bold uppercase tracking-wider text-gray-400">
                    Địa bàn
                  </th>
                  <th className="min-w-[160px] p-4 text-xs font-bold uppercase tracking-wider text-gray-400">
                    Liên hệ
                  </th>
                  <th className="min-w-[160px] p-4 text-xs font-bold uppercase tracking-wider text-gray-400">
                    Tags / Nguồn
                  </th>
                  <th className="min-w-[140px] p-4 text-xs font-bold uppercase tracking-wider text-gray-400">
                    Sales
                  </th>
                  <th className="min-w-[150px] p-4 text-xs font-bold uppercase tracking-wider text-gray-400">
                    Trạng thái
                  </th>
                  <th className="min-w-[130px] p-4 text-xs font-bold uppercase tracking-wider text-gray-400">
                    Cập nhật
                  </th>
                  {canDelete ? (
                    <th className="w-[64px] p-4 text-center text-xs font-bold uppercase tracking-wider text-gray-400">
                      Xóa
                    </th>
                  ) : null}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {customers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canDelete ? 9 : 8}
                      className="px-4 py-16 text-center text-sm font-semibold text-gray-500"
                    >
                      Không tìm thấy khách hàng phù hợp
                    </td>
                  </tr>
                ) : (
                  customers.map((c, index) => {
                  const recordStatus = c.record_status || "active";
                  const contactName = c.contact_name || "";
                  const contactPosition = c.contact_position || "";
                  const contactPhone = c.contact_phone || "";
                  const hasContact =
                    Boolean(contactName) ||
                    Boolean(contactPosition) ||
                    Boolean(contactPhone);
                  const mergedHint =
                    recordStatus === "merged" && c.successor_customer_name
                      ? ` → ${c.successor_customer_name}`
                      : "";
                  return (
                    <tr
                      key={c.id}
                      onClick={() => handleView(c.id)}
                      className="group cursor-pointer transition-colors hover:bg-red-50/30"
                    >
                      <td className="p-4 text-center text-sm font-medium italic text-gray-400">
                        {(page - 1) * pageSize + index + 1}
                      </td>

                      <td className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white bg-red-100 text-sm font-bold text-red-600">
                            {c.name?.charAt(0)?.toUpperCase() || "K"}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-bold text-gray-900 transition-colors group-hover:text-red-600">
                              {c.name || "—"}
                            </div>
                            <div className="mt-0.5 text-[11px] font-medium text-gray-500">
                              {[c.customer_type, c.school_level]
                                .filter(Boolean)
                                .join(" · ") || "Khách hàng"}
                            </div>
                            {c.customer_type === "Trường học" &&
                            c.student_count != null &&
                            c.student_count !== "" ? (
                              <div className="mt-0.5 text-[10px] font-semibold text-gray-400">
                                ~{c.student_count} học sinh
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>

                      <td className="p-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 text-[12px] font-bold text-gray-700">
                            <Building2 size={13} className="shrink-0 text-red-500" />
                            <span
                              className="max-w-[180px] truncate"
                              title={c.branch_name || undefined}
                            >
                              {c.branch_name || "—"}
                            </span>
                          </div>
                          <div
                            className="flex max-w-[200px] items-start gap-1.5 text-[11px] text-gray-500"
                            title={c.ward || undefined}
                          >
                            <MapPin size={12} className="mt-0.5 shrink-0 text-gray-400" />
                            <span className="line-clamp-2">
                              {c.ward || "—"}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td className="p-4">
                        {hasContact ? (
                          <div className="min-w-0 space-y-0.5">
                            <div
                              className="truncate text-[12px] font-bold text-gray-800"
                              title={contactName || undefined}
                            >
                              {contactName || "—"}
                            </div>
                            {contactPosition ? (
                              <div
                                className="truncate text-[11px] font-medium text-gray-500"
                                title={contactPosition}
                              >
                                {contactPosition}
                              </div>
                            ) : null}
                            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700">
                              <Phone size={12} className="shrink-0 text-red-500" />
                              <span className="truncate">
                                {contactPhone || "—"}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] font-medium italic text-gray-400">
                            Chưa có liên hệ
                          </span>
                        )}
                      </td>

                      <td className="p-4">
                        <div className="space-y-1.5">
                          <div className="flex flex-wrap gap-1">
                            {c.tags?.length > 0 ? (
                              <>
                                {c.tags.slice(0, 2).map((t) => (
                                  <span
                                    key={t.id}
                                    className="rounded-full px-2 py-[2px] text-[10px] font-bold text-white"
                                    style={{
                                      backgroundColor: t.color || "#9ca3af",
                                    }}
                                  >
                                    {t.name}
                                  </span>
                                ))}
                                {c.tags.length > 2 ? (
                                  <span className="rounded-full bg-gray-100 px-2 py-[2px] text-[10px] font-bold text-gray-500">
                                    +{c.tags.length - 2}
                                  </span>
                                ) : null}
                              </>
                            ) : (
                              <span className="text-[10px] font-medium italic text-gray-400">
                                Chưa có tags
                              </span>
                            )}
                          </div>
                          {c.lead_source ? (
                            <span className="inline-flex rounded border border-gray-200 bg-gray-50 px-2 py-[2px] text-[10px] font-bold uppercase text-gray-600">
                              {leadSourceLabels[c.lead_source] || c.lead_source}
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium italic text-gray-400">
                              Chưa có nguồn
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="p-4">
                        <div className="min-w-0 space-y-0.5">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-800">
                            <UserRound size={13} className="shrink-0 text-blue-500" />
                            <span
                              className="truncate"
                              title={c.owner_name || undefined}
                            >
                              {c.owner_name || "Chưa phân công"}
                            </span>
                          </div>
                          {c.owner_name && c.owner_position ? (
                            <div
                              className="truncate pl-[19px] text-[11px] font-medium text-gray-500"
                              title={c.owner_position}
                            >
                              {c.owner_position}
                            </div>
                          ) : null}
                        </div>
                      </td>

                      <td className="p-4">
                        <div className="flex flex-col items-start gap-1.5">
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                              statusBadgeClasses[c.status] ||
                              "border-gray-100 bg-gray-50 text-gray-500"
                            }`}
                          >
                            {c.status ? statusLabels[c.status] : "—"}
                          </span>
                          <span
                            className={`inline-flex max-w-[200px] truncate rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                              recordStatusBadgeClasses[recordStatus] ||
                              "border-gray-100 bg-gray-50 text-gray-500"
                            }`}
                            title={
                              mergedHint
                                ? `${recordStatusLabels[recordStatus]}${mergedHint}`
                                : undefined
                            }
                          >
                            {recordStatusLabels[recordStatus] || recordStatus}
                            {mergedHint}
                          </span>
                        </div>
                      </td>

                      <td className="p-4">
                        <div className="text-xs font-semibold text-gray-700">
                          {c.updated_at ? formatDateTimeVN(c.updated_at) : "—"}
                        </div>
                        {recordStatus !== "active" && c.closed_at ? (
                          <div className="mt-0.5 text-[10px] text-gray-400">
                            Đóng {formatDateTimeVN(c.closed_at)}
                          </div>
                        ) : null}
                      </td>

                      {canDelete ? (
                        <td className="p-4 text-center align-middle">
                          <button
                            type="button"
                            title="Xóa khách hàng"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(c.id);
                            }}
                            className="inline-flex items-center justify-center rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PAGINATION */}
      <Pagination
        totalRecords={total}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />

      {/* CREATE / EDIT MODAL */}
      <CustomerCreateModal
        isOpen={showDrawer}
        editingCustomer={editingCustomer}
        onClose={() => setShowDrawer(false)}
        onSuccess={refreshData}
      />

      <CustomerImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onRefresh={refreshData}
        branchOptions={importBranchOptions}
        defaultBranchId={defaultImportBranchId}
      />

      {/* DETAIL MODAL */}
      <CustomerDetailModal
        isOpen={showDetailModal}
        customer={detailCustomer}
        onClose={() => setShowDetailModal(false)}
        onEdit={handleEdit}
        onDelete={performDelete}
      />

      {/* TAGS MODAL */}
      <CustomerTagsModal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
      />
    </div>
  );
};

export default CustomerListPage;
